// ============================================================
//  USERS PAGE — ADMIN DASHBOARD
//  Path: src/pages/UsersPage.tsx
//
//  Access: super_admin, admin
//  Super admin can also reset any user's password
//
//  FIX (review pass):
//  - Reset PW and Delete are behind requireRecentAuth(5, ...) on the
//    backend (signed in within the last 5 minutes). The page never
//    re-verified the admin, so both failed after the first 5 minutes of a
//    session. They now go through useRecentAuth (password + authenticator
//    code first when needed), like AdminsPage.
//  - Search applies 300ms after typing stops (or on Enter) instead of per
//    keystroke, and a superseded request can't overwrite a newer one.
//  - A failed load no longer shows "No users found" / "0 total users".
//  - Activate/Deactivate: errors are shown (were console-only), Deactivate
//    asks first (it signs the person out), and customer_service — who can
//    list users but not change them — no longer sees the button.
//  - Delete requires typing DELETE, same as CustomersPage.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { listUsers, toggleUserActive, resetUserPassword, deleteUser } from '../services/admin.service'
import { useAuth } from '../context/AuthContext'
import { useRecentAuth } from '../hooks/useRecentAuth'

interface User {
  id: string; firstName: string; lastName: string; phone: string | null
  email: string | null; roles: string[]; isActive: boolean; createdAt: string
  // Included by the backend for each role the person has.
  customer?: { homeAddressCity?: string | null; homeAddressProvince?: string | null; homeAddressCountry?: string | null; city?: string | null } | null
  expert?: { addressCity?: string | null; addressProvince?: string | null; addressCountry?: string | null } | null
}

// Where the person is: a customer's home address, else a tasker's address.
const placeOf = (u: User) => ({
  country:  u.customer?.homeAddressCountry  || u.expert?.addressCountry  || null,
  province: u.customer?.homeAddressProvince || u.expert?.addressProvince || null,
  city:     u.customer?.homeAddressCity || u.customer?.city || u.expert?.addressCity || null,
})

const ADMIN_ROLES = ['super_admin', 'admin', 'customer_service']

const UsersPage: React.FC = () => {
  const { user: currentUser }       = useAuth()
  const [users, setUsers]           = useState<User[]>([])
  const [total, setTotal]           = useState(0)
  const [page, setPage]             = useState(1)
  // searchInput = what's typed; search = what's actually queried.
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch]         = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [loading, setLoading]       = useState(true)
  const [loadError, setLoadError]   = useState('')
  const [actionError, setActionError] = useState('')
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const requestSeq = useRef(0)
  const { runWithRecentAuth, reauthModal } = useRecentAuth()

  // Password reset state
  const [resetModal, setResetModal]       = useState<User | null>(null)
  const [resetPasswordVal, setResetPasswordVal] = useState('')
  const [resetError, setResetError]       = useState('')
  const [resetSuccess, setResetSuccess]   = useState('')
  const [resetting, setResetting]         = useState(false)

  // Delete state
  const [deleteModal, setDeleteModal]     = useState<User | null>(null)
  const [deleteError, setDeleteError]     = useState('')
  const [deleting, setDeleting]           = useState(false)

  const isSuperAdmin = currentUser?.roles.includes('super_admin')
  // PATCH /users/:id/active is super_admin / admin only.
  const canModifyUsers = isSuperAdmin || !!currentUser?.roles.includes('admin')

  const fetchUsers = useCallback(async () => {
    const seq = ++requestSeq.current
    setLoading(true)
    setLoadError('')
    try {
      const res = await listUsers({ page, limit: 20, search: search || undefined, role: roleFilter || undefined })
      if (seq !== requestSeq.current) return
      setUsers(res.data.data)
      setTotal(res.data.pagination.total)
    } catch (err: any) {
      if (seq !== requestSeq.current) return
      console.error('Failed to fetch users:', err)
      setUsers([])
      setTotal(0)
      setLoadError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load users.'))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [page, search, roleFilter])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  // Apply the typed search 300ms after typing stops.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  // Enter applies it immediately.
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput.trim())
    setPage(1)
  }

  const handleToggleActive = async (targetUser: User) => {
    if (!canToggle(targetUser)) return
    const deactivating = targetUser.isActive
    if (deactivating && !window.confirm(
      `Deactivate ${targetUser.firstName} ${targetUser.lastName}? They will be signed out and can't use the app until reactivated.`
    )) return

    setActionError('')
    setTogglingId(targetUser.id)
    try {
      await toggleUserActive(targetUser.id, !targetUser.isActive)
      setUsers((prev) => prev.map((u) => u.id === targetUser.id ? { ...u, isActive: !u.isActive } : u))
    } catch (err: any) {
      console.error('Failed to toggle user:', err)
      setActionError(err?.response?.data?.message ||
        `Failed to ${deactivating ? 'deactivate' : 'activate'} ${targetUser.firstName} ${targetUser.lastName} — try again.`)
    } finally {
      setTogglingId(null)
    }
  }

  const canToggle = (targetUser: User) => {
    if (!canModifyUsers) return false
    if (targetUser.id === currentUser?.id) return false
    if (targetUser.roles.includes('super_admin')) return false
    const targetIsAdmin = targetUser.roles.some(r => ADMIN_ROLES.includes(r))
    if (targetIsAdmin && !isSuperAdmin) return false
    return true
  }

  const canResetPw = (targetUser: User) => {
    if (!isSuperAdmin) return false
    if (targetUser.roles.includes('super_admin') && targetUser.id !== currentUser?.id) return false
    return true
  }

  // Delete is super_admin only, never self, never another super_admin,
  // never another admin account (use admin management panel for that).
  const canDelete = (targetUser: User) => {
    if (!isSuperAdmin) return false
    if (targetUser.id === currentUser?.id) return false
    if (targetUser.roles.some(r => ADMIN_ROLES.includes(r))) return false
    return true
  }

  const handleDeleteUser = () => {
    if (!deleteModal || deleteConfirmText !== 'DELETE') return
    const target = deleteModal
    setDeleteError('')
    runWithRecentAuth(async () => {
      setDeleting(true)
      try {
        await deleteUser(target.id)
        setDeleteModal(null)
        setDeleteConfirmText('')
        // Deleting the last row on a later page — step back a page.
        if (users.length === 1 && page > 1) setPage((p) => p - 1)
        else fetchUsers()
      } catch (err: any) {
        setDeleteError(err.response?.data?.message || 'Failed to delete user — try again')
      } finally {
        setDeleting(false)
      }
    })
  }

  const handleResetPassword = () => {
    if (!resetModal) return
    setResetError('')
    setResetSuccess('')

    if (resetPasswordVal.length < 8) {
      setResetError('Password must be at least 8 characters')
      return
    }

    const target = resetModal
    const newPassword = resetPasswordVal
    runWithRecentAuth(async () => {
      setResetting(true)
      try {
        await resetUserPassword(target.id, newPassword)
        setResetSuccess('Password reset successfully')
        setResetPasswordVal('')
        setTimeout(() => { setResetModal(null); setResetSuccess('') }, 1500)
      } catch (err: any) {
        setResetError(err.response?.data?.message || 'Failed to reset password')
      } finally {
        setResetting(false)
      }
    })
  }

  const totalPages = Math.ceil(total / 20)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">All Users</h1>
          <p className="text-gray-400 text-sm mt-1">{loadError ? '—' : `${total} total users`}</p>
        </div>
        {!canModifyUsers && (
          <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400">View Only</span>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex-1 min-w-[250px]">
          <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
        </form>
        <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1) }}
          className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none">
          <option value="">All Roles</option>
          <option value="customer">Customer</option>
          <option value="expert">Expert</option>
          <option value="admin">Admin</option>
          <option value="customer_service">Customer Service</option>
          <option value="super_admin">Super Admin</option>
        </select>
      </div>

      {loadError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{loadError}</p>
          <button onClick={() => fetchUsers()}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      )}
      {actionError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {actionError}
          <button onClick={() => setActionError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
        </div>
      )}

      <div className="bg-surface-card border border-border-light rounded-2xl overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-light">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Name</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Contact</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Country</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Province</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">City</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Roles</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Joined</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td></tr>
            ) : loadError ? (
              <tr><td colSpan={9} className="text-center py-8 text-gray-500">—</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-gray-400">No users found</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-b border-border-light hover:bg-surface-tertiary/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-brand-700 flex items-center justify-center text-white text-sm font-bold">
                        {u.firstName?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{u.firstName} {u.lastName}</p>
                        <p className="text-xs text-gray-500">{u.id.slice(0, 12)}...</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-gray-300">{u.email || '—'}</p>
                    <p className="text-xs text-gray-500">{u.phone || '—'}</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">{placeOf(u).country || '—'}</td>
                  <td className="px-5 py-4 text-sm text-gray-300">{placeOf(u).province || '—'}</td>
                  <td className="px-5 py-4 text-sm text-gray-300">{placeOf(u).city || '—'}</td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <span key={r} className={`px-2 py-0.5 text-xs rounded-full ${
                          r === 'super_admin' ? 'bg-purple-600/20 text-purple-400' :
                          ADMIN_ROLES.includes(r) ? 'bg-brand-600/20 text-brand-400' :
                          'bg-gray-600/20 text-gray-400'
                        }`}>{r.replace('_', ' ')}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      u.isActive ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                    }`}>{u.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2">
                      {canToggle(u) && (
                        <button onClick={() => handleToggleActive(u)} disabled={togglingId === u.id}
                          className={`px-3 py-1.5 text-xs rounded-lg disabled:opacity-50 transition-colors ${
                            u.isActive ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                          }`}>{togglingId === u.id ? 'Saving...' : u.isActive ? 'Deactivate' : 'Activate'}</button>
                      )}
                      {canResetPw(u) && (
                        <button onClick={() => { setResetModal(u); setResetPasswordVal(''); setResetError(''); setResetSuccess('') }}
                          className="px-3 py-1.5 text-xs bg-yellow-500/10 text-yellow-400 rounded-lg hover:bg-yellow-500/20 transition-colors">
                          Reset PW
                        </button>
                      )}
                      {canDelete(u) && (
                        <button onClick={() => { setDeleteModal(u); setDeleteError(''); setDeleteConfirmText('') }}
                          className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">
                          Delete
                        </button>
                      )}
                      {!canToggle(u) && !canResetPw(u) && !canDelete(u) && (
                        <span className="text-xs text-gray-600">
                          {u.roles.includes('super_admin') ? 'Protected' : u.id === currentUser?.id ? 'You' : '—'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border-light">
            <p className="text-sm text-gray-400">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Previous</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-2">Reset Password</h3>
            <p className="text-sm text-gray-400 mb-4">
              Set a new password for {resetModal.firstName} {resetModal.lastName}
              <span className="text-gray-500 ml-1">({resetModal.email || resetModal.phone || resetModal.id.slice(0, 12)})</span>
            </p>

            {resetError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-sm">{resetError}</p>
              </div>
            )}
            {resetSuccess && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
                <p className="text-green-400 text-sm">{resetSuccess}</p>
              </div>
            )}

            <input type="password" placeholder="New password (min 8 characters)" value={resetPasswordVal}
              onChange={(e) => setResetPasswordVal(e.target.value)} minLength={8}
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4" />

            <div className="flex gap-3 justify-end">
              <button onClick={() => setResetModal(null)}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button onClick={handleResetPassword} disabled={resetting}
                className="px-4 py-2 text-sm bg-yellow-600 text-white rounded-xl hover:bg-yellow-500 disabled:opacity-50 transition-colors">
                {resetting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-2">Delete User Permanently</h3>
            <p className="text-sm text-gray-400 mb-4">
              This will permanently delete <span className="text-white font-medium">{deleteModal.firstName} {deleteModal.lastName}</span> and
              everything attached to their account — documents, categories, tasks, reviews, payments. This cannot be undone.
            </p>

            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
              <p className="text-red-400 text-xs font-medium">
                ⚠️ Permanent — there is no undo. The user's Firebase account and all database records will be wiped.
              </p>
            </div>

            {deleteError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-sm">{deleteError}</p>
              </div>
            )}

            <label className="block text-xs text-gray-500 mb-1">Type DELETE to confirm</label>
            <input type="text" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} autoFocus
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-red-500 focus:outline-none mb-4" />

            <div className="flex gap-3 justify-end">
              <button onClick={() => { setDeleteModal(null); setDeleteConfirmText('') }}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">
                Cancel
              </button>
              <button onClick={handleDeleteUser} disabled={deleting || deleteConfirmText !== 'DELETE'}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-xl hover:bg-red-500 disabled:opacity-50 transition-colors">
                {deleting ? 'Deleting...' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reauthModal}
    </div>
  )
}

export default UsersPage